import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Star, Home as HomeIcon, Building, RotateCcw, Camera, MapPin } from 'lucide-react';
import { portfolioProjects, blogPosts } from '../data/mockData';
import { publicAPI } from '../services/apiService';
import { getImageUrl } from '../utils/imageUtils';
import { locationsManager } from '../utils/locationsManager';
import SEO from '../components/SEO';
import PageTitle from '../components/PageTitle';
import BeforeAfterSection from '../components/BeforeAfterSection';

const Home = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [realPortfolio, setRealPortfolio] = useState([]);
  const [realBlog, setRealBlog] = useState([]);
  const [siteImages, setSiteImages] = useState({});
  const [siteBranding, setSiteBranding] = useState({
    site_name: 'FotografX',
    site_description_de: 'Professioneller Fotograf in Gera und Ronneburg - Immobilienfotografie, Hochzeiten, Portraits und Events',
    site_description_ru: 'Профессиональный фотограф в Гере и Роннебурге - Недвижимость, свадьбы, портреты и мероприятия'
  });
  const [activeLocations, setActiveLocations] = useState([]);

  // Load real data from API
  useEffect(() => {
    loadRealData();
  }, []);

  const loadRealData = async () => {
    try {
      // Load reviews
      const reviewsResponse = await publicAPI.getReviews();
      setFeaturedReviews(reviewsResponse.data.filter(review => review.approved).slice(0, 3));
      
      // Load portfolio
      const portfolioResponse = await publicAPI.getPortfolio();
      setRealPortfolio(portfolioResponse.data.filter(project => project.featured).slice(0, 3));
      
      // Load blog
      const blogResponse = await publicAPI.getBlog();
      setRealBlog(blogResponse.data.slice(0, 2));

      // Load site images
      const imagesResponse = await publicAPI.getSiteImages();
      setSiteImages(imagesResponse.data);

      // Load site branding
      const brandingResponse = await publicAPI.getSiteBranding();
      setSiteBranding(brandingResponse.data);

      // Load locations
      const allLocations = await locationsManager.initializeDefaultLocations();
      const activeLocationsData = allLocations.filter(loc => loc.active);
      setActiveLocations(activeLocationsData);
    } catch (error) {
      console.error('Error loading homepage data:', error);
      // Fallback to mock data if API fails
      setFeaturedReviews([]);
      // Fallback to default locations if API fails
      setActiveLocations([
        { id: 'gera', name: 'Gera', slug: 'gera', active: true },
        { id: 'ronneburg', name: 'Ronneburg', slug: 'ronneburg', active: true },
        { id: 'chemnitz', name: 'Chemnitz', slug: 'chemnitz', active: true },
        { id: 'erfurt', name: 'Erfurt', slug: 'erfurt', active: true },
        { id: 'leipzig', name: 'Leipzig', slug: 'leipzig', active: true },
        { id: 'jena', name: 'Jena', slug: 'jena', active: true },
        { id: 'zwickau', name: 'Zwickau', slug: 'zwickau', active: true }
      ]);
    }
  };

  // Use real data if available, fallback to mock data
  const featuredProjects = realPortfolio.length > 0 ? realPortfolio : portfolioProjects.filter(project => project.featured).slice(0, 3);
  const latestPosts = realBlog.length > 0 ? realBlog : blogPosts.slice(0, 2);

  // Structured data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": siteBranding.site_name || "FotografX",
    "image": `${window.location.origin}/favicon-32x32.png`,
    "description": currentLang === 'de' 
      ? siteBranding.site_description_de || "Professioneller Fotograf in Gera, Ronneburg & Umgebung - Hochzeiten, Portraits, Immobilienfotografie und Events"
      : siteBranding.site_description_ru || "Фотограф в Гере (Gera) и Роннебурге - FotografX – профессиональный фотограф в Гере, Роннебурге, Йене, Хемнице, Эрфурте и Лейпциге. Свадебная, портретная и семейная фотосъёмка, а также фотосъёмка недвижимости.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Gera",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "52.5200",
      "longitude": "13.4050"
    },
    "telephone": "+49 (0) 123 456 789",
    "email": "info@fotografx.de",
    "url": window.location.origin,
    "sameAs": [
      process.env.REACT_APP_INSTAGRAM_URL || "https://www.instagram.com/fotografx",
      process.env.REACT_APP_FACEBOOK_URL || "https://www.facebook.com/fotografx"
    ],
    "openingHours": "Mo-Fr 09:00-18:00, Sa 10:00-16:00",
    "priceRange": "€€€",
    "areaServed": "Berlin",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Photography Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": currentLang === 'de' ? "Hochzeitsfotografie" : "Wedding Photography"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": currentLang === 'de' ? "Portraitfotografie" : "Portrait Photography"
          }
        }
      ]
    }
  };

  return (
    <div className="bg-black text-white">
      <PageTitle 
        title={t('pageTitles.home')}
        description={currentLang === 'de' 
          ? `${siteBranding.site_name} - Professioneller Fotograf in Gera und Ronneburg für Immobilienfotografie, Hochzeiten, Portraits und Events. ${siteBranding.site_description_de}`
          : `${siteBranding.site_name} - Professional photographer in Gera and Ronneburg for immobilienphoto, weddings, portraits and events. ${siteBranding.site_description_ru}`
        }
      />
      <SEO
        title={currentLang === 'de' ? "Professionelle Immobilienfotografie in Gera und Umgebung | FotografX" : "Профессиональная фотография недвижимости в Германии. Гера и окрестностях | FotografX"}
        description={currentLang === 'de' 
          ? `${siteBranding.site_name} - Professionelle Immobilienfotografie in Thüringen. Hochwertige Innenaufnahmen, Außenaufnahmen, Architekturfotos und 360° virtuelle Rundgänge für Makler und Eigentümer. ${siteBranding.site_description_de}`
          : `${siteBranding.site_name} - Профессиональная фотография недвижимости в Тюрингии. Качественные интерьерные съемки, экстерьеры, архитектурные фото и виртуальные туры 360° для риелторов и собственников. ${siteBranding.site_description_ru}`
        }
        structuredData={structuredData}
        type="website"
        url="/"
      />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0 bg-black opacity-50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url('${siteImages.hero_background_image || 'https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjBkZXNpZ24lMjBsdXh1cnl8ZW58MHx8fHwxNzY4OTI1OTg4fDA&ixlib=rb-4.1.0&q=85'}')`
          }}
        ></div>
        <div className="relative z-20 text-center px-4 sm:px-6 lg:px-8">
          <h1 
            className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' 
              ? (siteBranding.hero_title_de || 'Professioneller Immobilienfotograf in Thüringen')
              : (siteBranding.hero_title_ru || 'Профессиональный фотограф недвижимости в Тюрингии')
            }
          </h1>
          <div className="mb-8">
            <p className="text-xl md:text-2xl mb-4 max-w-3xl mx-auto text-gray-200">
              {currentLang === 'de' 
                ? 'Professionelle Immobilienfotografie für Verkauf und Vermietung. Hochwertige Innen- und Außenaufnahmen, Architekturfotos und 360° virtuelle Rundgänge.'
                : 'Профессиональная фотография недвижимости для продажи и аренды. Качественные интерьерные и экстерьерные съемки, архитектурные фото и виртуальные туры 360°.'
              }
            </p>
            <p className="text-lg md:text-xl max-w-2xl mx-auto text-gray-300">
              {siteBranding.site_name || 'FotografX'}
            </p>
          </div>
          <Link 
            to="/portfolio?category=immobilien"
            className="inline-flex items-center px-8 py-4 bg-white text-black font-semibold text-lg hover:bg-gray-200 transition-all duration-300 transform hover:scale-105"
          >
            {currentLang === 'de' ? 'Immobilien-Portfolio' : 'Портфолио недвижимости'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
          <Link 
            to="/calculator"
            className="ml-4 inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold text-lg hover:bg-white hover:text-black transition-all duration-300"
          >
            {currentLang === 'de' ? 'Preis berechnen' : 'Рассчитать цену'}
          </Link>
        </div>
      </section>

      {/* About Real Estate Photography Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-8 text-gray-800"
            style={{ fontFamily: '"Times New Roman", serif' }}
          >
            {currentLang === 'de' 
              ? 'Ihre Immobilie in bestem Licht'
              : 'Ваша недвижимость в лучшем свете'
            }
          </h2>
          <div className="text-lg text-gray-700 max-w-3xl mx-auto">
            {siteBranding.real_estate_expertise_de && currentLang === 'de' ? (
              <div dangerouslySetInnerHTML={{ __html: siteBranding.real_estate_expertise_de }} />
            ) : siteBranding.real_estate_expertise_ru && currentLang === 'ru' ? (
              <div dangerouslySetInnerHTML={{ __html: siteBranding.real_estate_expertise_ru }} />
            ) : (
              <div>
                <p className="mb-4">
                  {currentLang === 'de'
                    ? 'Professionelle Immobilienfotografie für erfolgreichen Verkauf und Vermietung. Mit über 5 Jahren Erfahrung in der Architekturfotografie präsentiere ich Ihre Immobilie von ihrer besten Seite.'
                    : 'Профессиональная фотография недвижимости для успешной продажи и аренды. С более чем 5-летним опытом в архитектурной фотографии я представлю вашу недвижимость с лучшей стороны.'
                  }
                </p>
                <p className="mb-6">
                  {currentLang === 'de'
                    ? 'Von stilvollen Innenaufnahmen bis hin zu beeindruckenden Außenansichten - ich erstelle hochwertige Bilder, die Käufer und Mieter begeistern. Inklusive 360° virtueller Rundgänge für maximale Präsentation.'
                    : 'От стильных интерьерных снимков до впечатляющих внешних видов - я создаю качественные изображения, которые привлекают покупателей и арендаторов. Включая виртуальные туры 360° для максимальной презентации.'
                  }
                </p>
              </div>
            )}
          </div>
          <div className="mt-8">
            <Link 
              to="/booking"
              className="inline-flex items-center px-6 py-3 bg-black text-white font-semibold hover:bg-gray-800 transition-all duration-300"
            >
              {currentLang === 'de' ? 'Jetzt buchen' : 'Забронировать съемку'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Real Estate Services Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-800" style={{ fontFamily: '"Times New Roman", serif' }}>
              {currentLang === 'de' ? 'Immobilienfotografie-Services' : 'Услуги фотографии недвижимости'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentLang === 'de' 
                ? 'Komplette Fotolösungen für Ihre Immobilie - von klassischen Aufnahmen bis zu modernen virtuellen Rundgängen'
                : 'Комплексные фотографические решения для вашей недвижимости - от классических снимков до современных виртуальных туров'
              }
            </p>
            {/* Price indicator */}
            <div className="mt-6 inline-flex items-center px-6 py-3 bg-gray-900 text-white rounded-full">
              <span className="text-2xl font-bold mr-2">€10</span>
              <span className="text-gray-300">
                {currentLang === 'de' ? 'pro Foto' : 'за фото'}
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Interior Photography */}
            <div className="text-center group">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors duration-300">
                <HomeIcon className="w-8 h-8 text-gray-600 group-hover:text-gray-800 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {currentLang === 'de' ? 'Innenaufnahmen' : 'Интерьерная съемка'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLang === 'de' 
                  ? 'Professionelle Fotografie von Wohnräumen, Küchen, Badezimmern und allen Innenräumen'
                  : 'Профессиональная фотография жилых комнат, кухонь, ванных и всех интерьеров'
                }
              </p>
            </div>

            {/* Exterior Photography */}
            <div className="text-center group">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors duration-300">
                <Building className="w-8 h-8 text-gray-600 group-hover:text-gray-800 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {currentLang === 'de' ? 'Außenaufnahmen' : 'Экстерьерная съемка'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLang === 'de' 
                  ? 'Beeindruckende Außenansichten, Gärten, Terrassen und Fassadenaufnahmen'
                  : 'Впечатляющие внешние виды, сады, террасы и фасадные снимки'
                }
              </p>
            </div>

            {/* 360° Virtual Tours */}
            <div className="text-center group">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors duration-300">
                <RotateCcw className="w-8 h-8 text-gray-600 group-hover:text-gray-800 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {currentLang === 'de' ? 'Virtuelle Rundgänge 360°' : 'Виртуальные туры 360°'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLang === 'de' 
                  ? 'Interaktive 360°-Touren für immersive Online-Besichtigungen'
                  : 'Интерактивные туры 360° для погружающих онлайн-просмотров'
                }
              </p>
            </div>

            {/* Architectural Photography */}
            <div className="text-center group">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-200 transition-colors duration-300">
                <Camera className="w-8 h-8 text-gray-600 group-hover:text-gray-800 transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-gray-800">
                {currentLang === 'de' ? 'Architekturfotografie' : 'Архитектурная фотография'}
              </h3>
              <p className="text-gray-600 text-sm">
                {currentLang === 'de' 
                  ? 'Künstlerische Aufnahmen von Gebäudearchitektur und Designdetails'
                  : 'Художественные снимки архитектуры зданий и деталей дизайна'
                }
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-lg text-gray-600 mb-6">
              {currentLang === 'de' 
                ? 'Zusätzliche Dienstleistungen: Hochzeitsfotografie, Portraits und Event-Fotografie'
                : 'Дополнительные услуги: свадебная фотография, портреты и съемка мероприятий'
              }
            </p>
            <div className="flex justify-center">
              <Link 
                to="/portfolio?category=real-estate"
                className="inline-flex items-center px-8 py-4 bg-gray-800 text-white font-semibold hover:bg-gray-900 transition-all duration-300 transform hover:scale-105"
              >
                {currentLang === 'de' ? 'Immobilien-Portfolio ansehen' : 'Посмотреть портфолио недвижимости'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Comparison Section */}
      <BeforeAfterSection />

      {/* Real Estate Portfolio Gallery */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {currentLang === 'de' ? 'Immobilien Portfolio' : 'Портфолио недвижимости'}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {currentLang === 'de' 
                ? 'Professionelle Immobilienfotografie - Innenräume, Außenaufnahmen und Architekturfotografie in Thüringen'
                : 'Профессиональная фотография недвижимости - интерьеры, экстерьеры и архитектурная фотография в Тюрингии'
              }
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.filter(project => 
              project.category === 'real-estate' || 
              project.category === 'interior' || 
              project.category === 'exterior' ||
              project.category === 'architecture'
            ).map((project) => (
              <div 
                key={project.id} 
                className="group cursor-pointer bg-gray-900 overflow-hidden transition-all duration-300 hover:transform hover:scale-105"
              >
                <div className="aspect-w-16 aspect-h-12 overflow-hidden">
                  <img 
                    src={getImageUrl(project.image)} 
                    alt={`${project[`title_${currentLang}`]} - ${project[`description_${currentLang}`]}`}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 
                    className="text-xl font-semibold mb-2"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {project[`title_${currentLang}`]}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {project[`description_${currentLang}`]}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/portfolio"
              className="inline-flex items-center px-6 py-3 border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
            >
              {currentLang === 'de' ? 'Alle Arbeiten ansehen' : 'Посмотреть все работы'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section - Hidden until real reviews are available 
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('home.reviewsTitle')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('home.reviewsSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredReviews.map((review) => (
              <div key={review.id} className="bg-black p-6 border border-gray-800">
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">
                  "{review[`text_${currentLang}`]}"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-white">{review.name}</p>
                  <p className="text-gray-400">{review.service}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Blog Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('home.blogTitle')}
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('home.blogSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {latestPosts.map((post) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.id}`}
                className="group bg-gray-900 overflow-hidden hover:bg-gray-800 transition-all duration-300"
              >
                <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                  <img 
                    src={getImageUrl(post.image)} 
                    alt={`${post[`title_${currentLang}`]} - Blog post by ${post.author}`}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-gray-400 mb-2">{post.date}</div>
                  <h3 
                    className="text-xl font-semibold mb-3 group-hover:text-gray-300 transition-colors duration-300"
                    style={{ fontFamily: '"Times New Roman", serif' }}
                  >
                    {post[`title_${currentLang}`]}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {post[`excerpt_${currentLang}`]}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link 
              to="/blog"
              className="inline-flex items-center px-6 py-3 border border-white text-white hover:bg-white hover:text-black transition-all duration-300"
            >
              {currentLang === 'de' ? 'Weitere Artikel lesen' : 'Читать больше статей'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4 text-gray-800 flex items-center justify-center gap-3"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              <MapPin className="w-8 h-8 text-gray-600" />
              {currentLang === 'de' ? 'Standorte' : 'Локации'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {currentLang === 'de' 
                ? 'Professionelle Immobilienfotografie in Gera und den umliegenden Städten Thüringens'
                : 'Профессиональная фотография недвижимости в Гере и окружающих городах Тюрингии'
              }
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {activeLocations.length > 0 ? activeLocations.map((location) => (
              <Link
                key={location.slug}
                to={`/${location.slug}`}
                className="group p-6 bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 text-center border border-gray-200 hover:border-gray-300"
              >
                <div className="text-xl font-bold text-gray-800 group-hover:text-black transition-colors mb-2">
                  {location.name}
                </div>
                <div className="text-sm text-gray-500 mb-3">
                  {currentLang === 'de' ? 'Immobilienfotograf' : 'Фотограф недвижимости'}
                </div>
                <div className="text-xs text-gray-400 group-hover:text-gray-600 transition-colors">
                  {currentLang === 'de' ? 'Verfügbar für Projekte' : 'Доступен для проектов'}
                </div>
              </Link>
            )) : (
              <div className="col-span-full">
                <div className="text-center text-gray-500 py-12">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
                    {['Gera', 'Jena', 'Chemnitz', 'Erfurt', 'Zwickau'].map((city) => (
                      <Link
                        key={city.toLowerCase()}
                        to={`/${city.toLowerCase()}`}
                        className="p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 text-center border border-gray-200 hover:border-gray-300 group"
                      >
                        <div className="text-lg font-semibold text-gray-800 group-hover:text-black">
                          {city}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {currentLang === 'de' ? 'Immobilienfotografie' : 'Фото недвижимости'}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-600">
              {currentLang === 'de' 
                ? 'Weitere Regionen auf Anfrage - kontaktieren Sie mich für individuelle Angebote'
                : 'Другие регионы по запросу - свяжитесь со мной для индивидуальных предложений'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;